import { computed, type MaybeRefOrGetter, toValue, watch } from 'vue'
import { useQuery, useQueryCache, type QueryCache } from '@pinia/colada'
import { getItemAssociations, type ItemAssociationRow } from '@/api/lytAccountingClient'
import { useAuthStore } from '@/stores/authStore'

/** Association status filter accepted by the items associations list API. */
export type ItemAssociationStatusFilter = 'all' | 'associated' | 'unassociated' | 'syncing'

export type ItemAssociationsQueryParams = {
  page: MaybeRefOrGetter<number>
  pageSize: MaybeRefOrGetter<number>
  status: MaybeRefOrGetter<ItemAssociationStatusFilter>
}

/**
 * Stable cache key for item association list reads.
 * Includes entity domain, authenticated integration/store identity when present,
 * pagination, and status — never API keys or other secrets.
 */
export function itemAssociationsQueryKey(parts: {
  integrationId: number | null
  storeId: string | null
  page: number
  pageSize: number
  status: ItemAssociationStatusFilter
}): readonly [
  'item-associations',
  number | null,
  string | null,
  number,
  number,
  ItemAssociationStatusFilter,
] {
  return [
    'item-associations',
    parts.integrationId,
    parts.storeId,
    parts.page,
    parts.pageSize,
    parts.status,
  ] as const
}

/**
 * Invalidate every item-associations query entry (all pages/filters).
 * Used after successful link or single-sync so the active list refetches.
 */
export function invalidateItemAssociationsQueries(
  queryCache: QueryCache = useQueryCache(),
): Promise<unknown> {
  return queryCache.invalidateQueries({ key: ['item-associations'] })
}

/**
 * Pinia Colada read boundary for Lytning item association lists.
 * Pages own filter/page refs; this composable owns the HTTP read + cache key.
 */
export function useItemAssociationsQuery(params: ItemAssociationsQueryParams) {
  const auth = useAuthStore()

  const query = useQuery({
    key: () =>
      itemAssociationsQueryKey({
        integrationId: auth.currentIntegration?.integrationId ?? null,
        storeId: auth.selectedStore?.storeId ?? (auth.storeID || null),
        page: toValue(params.page),
        pageSize: toValue(params.pageSize),
        status: toValue(params.status),
      }),
    query: () => {
      const status = toValue(params.status)
      return getItemAssociations({
        page: toValue(params.page),
        pageSize: toValue(params.pageSize),
        status: status === 'all' ? undefined : status,
      })
    },
  })

  const rows = computed<ItemAssociationRow[]>(() => query.data.value?.items ?? [])
  const total = computed(() => query.data.value?.total ?? 0)
  const errorMessage = computed(() => {
    const err = query.error.value
    if (!err) return null
    return err instanceof Error ? err.message : String(err)
  })

  // Stamp last successful fetch time without mutating query cache.
  const lastSuccessAt = { current: null as Date | null }
  watch(
    () => query.status.value,
    (status) => {
      if (status === 'success') {
        lastSuccessAt.current = new Date()
      }
    },
  )

  return {
    rows,
    total,
    isLoading: query.isLoading,
    error: query.error,
    errorMessage,
    status: query.status,
    refetch: query.refetch,
    refresh: query.refresh,
    /** Timestamp of the last successful association list fetch in this scope. */
    get lastRefreshAt() {
      return lastSuccessAt.current
    },
    lastRefreshAtRef: computed(() => lastSuccessAt.current),
  }
}
